import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { getOfflineQueue } from "@/lib/offline";
import { useI18n } from "@/contexts/I18nContext";
import CashBoxPicker from "@/components/CashBoxPicker";
import RetailPOSPaymentDialog from "./components/RetailPOSPaymentDialog";
import { printReceipt as printThermal, isSupported as thermalSupported } from "@/lib/thermalPrinter";
import { useAuth } from "@/contexts/AuthContext";
import { useFeature } from "@/contexts/FeaturesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ScanBarcode, Trash2, Plus, Minus, Printer, ShoppingCart, Search, Camera, X, Zap, Keyboard, CreditCard, Banknote, Landmark, PauseCircle, RotateCcw, Flame, CloudOff, CheckCircle2 } from "lucide-react";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * RetailPOS - streamlined cashier interface for retail/point-of-sale.
 * Focus: speed. No warehouses, VAT overrides, currencies, or complex fields.
 * All defaults auto-applied from company settings.
 */

// Weight-based selling: when a product's unit is a kilogram, the cashier can pick a
// quick weight (fraction of a kilo) so the price is computed automatically and the
// exact weight is deducted from stock. e.g. 200غ -> quantity 0.2 kg.
const WEIGHT_UNIT_KEYWORDS = ["كيلو", "كغ", "كجم", "kg", "kilo", "فرط", "وزن"];
const isWeightUnit = (name) => {
  const n = (name || "").toString().trim().toLowerCase();
  return WEIGHT_UNIT_KEYWORDS.some(k => n.includes(k));
};
const WEIGHT_PRESETS = [
  { label: "1 كغ", v: 1 },
  // 50غ → 900غ بخطوة 50غ
  ...Array.from({ length: 18 }, (_, i) => {
    const g = 50 * (i + 1);
    return { label: `${g}غ`, v: g / 1000 };
  }),
];
// أزرار «بالقيمة المالية»: يختار الكاشير مبلغاً فيُحسب الوزن تلقائياً (المبلغ ÷ سعر الكيلو)
const MONEY_PRESETS = [50, 100, 150, 200];

// Parse an electronic-scale (weight/price embedded) barcode into { itemCode, weightKg | price }.
// Returns null when the code doesn't match the configured scale format (falls back to normal scan).
const parseScaleBarcode = (raw, cfg) => {
  if (!cfg || !cfg.enabled) return null;
  const code = (raw || "").trim();
  const prefix = String(cfg.prefix || "2");
  if (!/^\d+$/.test(code)) return null;
  if (code.length < 12 || code.length > 13) return null;
  if (!code.startsWith(prefix)) return null;
  const itemDigits = Number(cfg.item_digits || 5);
  const valueDigits = Number(cfg.value_digits || 5);
  const start = prefix.length;
  if (start + itemDigits + valueDigits > code.length) return null;
  const itemCode = code.slice(start, start + itemDigits);
  const valueNum = parseInt(code.slice(start + itemDigits, start + itemDigits + valueDigits), 10);
  if (Number.isNaN(valueNum)) return null;
  if (cfg.mode === "price") return { itemCode, price: valueNum / Number(cfg.price_divisor || 100) };
  return { itemCode, weightKg: valueNum / Number(cfg.weight_divisor || 1000) };
};

export default function RetailPOS() {
  const { currentCompany, user } = useAuth();
  const quickProductAddEnabled = useFeature("quick_product_add");
  const { t } = useI18n();
  const ti = (key, vars = {}) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v), t(key));
  const nav = useNavigate();
  const [lines, setLines] = useState([]);
  const [barcodeVal, setBarcodeVal] = useState("");
  const [products, setProducts] = useState([]);
  const [rates, setRates] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [camOpen, setCamOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [payMethod, setPayMethod] = useState("cash"); // cash | credit
  const [received, setReceived] = useState("");
  // Payment box: defaults to base currency + null box_id (backend auto-picks default box).
  // Cashier can override via CashBoxPicker in the payment dialog.
  const [paymentBox, setPaymentBox] = useState({ currency: null, cash_box_id: null });

  // Keep payment box currency aligned with company's base currency whenever it changes
  useEffect(() => {
    if (currentCompany?.base_currency && !paymentBox.currency) {
      setPaymentBox({ currency: currentCompany.base_currency, cash_box_id: null });
    }
    // eslint-disable-next-line
  }, [currentCompany?.base_currency]);
  const [busy, setBusy] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null);  // {code, discount_percent}
  // Credit-sale customer (آجل): a credit sale must be attached to a customer so the
  // outstanding amount is added to that customer's account.
  const [customers, setCustomers] = useState([]);
  const [custId, setCustId] = useState(null);
  const [custName, setCustName] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [creatingCust, setCreatingCust] = useState(false);
  // Bank transfer (حوالة بنكية): money is deposited into a selected bank account.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankId, setBankId] = useState("");
  const scanRef = useRef(null);
  const searchRef = useRef(null);

  // Top sellers + parked (held) sales
  const [topSellers, setTopSellers] = useState([]);
  const heldKey = `pos_held_${currentCompany?.id || "x"}`;
  const [held, setHeld] = useState([]);
  const [heldOpen, setHeldOpen] = useState(false);

  useEffect(() => {
    try { const raw = localStorage.getItem(heldKey); setHeld(raw ? JSON.parse(raw) : []); } catch { setHeld([]); }
    // eslint-disable-next-line
  }, [currentCompany?.id]);

  const persistHeld = (arr) => { setHeld(arr); try { localStorage.setItem(heldKey, JSON.stringify(arr)); } catch {} };
  const holdSale = () => {
    if (!lines.length) { toast.error(t("pos_toast_no_items_hold")); return; }
    const entry = { id: Date.now(), at: new Date().toISOString(), count: lines.length, total: lines.reduce((s, l) => s + (l.total || 0), 0), currency: saleCurrency, lines };
    persistHeld([entry, ...held]);
    setLines([]); setVoucher(null); setVoucherCode("");
    toast.success(t("pos_toast_held"));
  };
  const resumeSale = (h) => {
    if (lines.length && !window.confirm(t("pos_toast_resume_confirm"))) return;
    setLines(h.lines || []);
    persistHeld(held.filter(x => x.id !== h.id));
    setHeldOpen(false);
    toast.success(t("pos_toast_resumed"));
  };
  const deleteHeld = (id) => persistHeld(held.filter(x => x.id !== id));

  // Bulk-save selected held (parked) sales as cash invoices at once → reflects on the cash box.
  const [selHeld, setSelHeld] = useState([]);
  const [savingHeld, setSavingHeld] = useState(false);
  const heldAllSelected = held.length > 0 && selHeld.length === held.length;
  const toggleHeldOne = (id) => setSelHeld(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleHeldAll = () => setSelHeld(() => (held.length > 0 && selHeld.length === held.length) ? [] : held.map(h => h.id));
  const saveHeldSelected = async () => {
    const chosen = held.filter(h => selHeld.includes(h.id));
    if (!chosen.length) { toast.error("لم تحدّد أي فاتورة معلّقة"); return; }
    setSavingHeld(true);
    let ok = 0, fail = 0; const savedIds = [];
    for (const h of chosen) {
      const cur = (h.currency || baseCurrency).toUpperCase();
      const rate = cur === baseCurrency ? 1 : convertAmount(1, cur, baseCurrency);
      const hTotal = (h.lines || []).reduce((s, l) => s + (l.total || 0), 0);
      const body = {
        kind: "sale", party_id: null, party_name: "عميل نقدي",
        payment_type: "cash", paid_amount: hTotal,
        discount_percent: 0, discount_amount: 0,
        currency: cur, exchange_rate: rate,
        lines: h.lines || [], notes: "",
        cash_box_id: paymentBox.cash_box_id || user?.default_cash_box_id || null,
        bank_account_id: null, payments: null, return_reason: null, ref_invoice_id: null,
        warehouse_id: (costCenters.find(c => c.name === costCenter)?.warehouse_id) || null,
        cost_center: costCenter || "", vat_rate: null,
        idempotency_key: (window.crypto?.randomUUID && window.crypto.randomUUID()) || `pos-held-${h.id}-${Math.random().toString(36).slice(2)}`,
      };
      try { await api.post("/invoices", body); ok++; savedIds.push(h.id); }
      catch (e) {
        if (e?.code === "OFFLINE_QUEUED") { ok++; savedIds.push(h.id); } // queued offline = saved, will sync
        else fail++;
      }
    }
    persistHeld(held.filter(h => !savedIds.includes(h.id)));
    setSelHeld(s => s.filter(id => !savedIds.includes(id)));
    setSavingHeld(false);
    if (ok) { toast.success(`تم حفظ ${ok} فاتورة معلّقة وانعكست على الصندوق`); loadProducts(); loadTopSellers(); }
    if (fail) toast.warning(`تعذّر حفظ ${fail} فاتورة — حاول مجدداً`);
  };

  // Quick miscellaneous (cash) item — sell an amount without a product/barcode; no stock impact
  const [miscOpen, setMiscOpen] = useState(false);
  const [misc, setMisc] = useState({ name: "", price: "" });
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", barcode: "", sale_price: "", cost_price: "", stock: "", category: "" });
  const [newProductBusy, setNewProductBusy] = useState(false);
  const addMiscItem = () => {
    const price = Number(misc.price) || 0;
    if (price <= 0) { toast.error(t("pos_toast_valid_amount")); return; }
    const name = misc.name.trim() || t("pos_misc_default");
    setLines(ls => [...ls, { product_id: "", product_name: name, barcode: "", unit_name: "قطعة", unit_factor: 1, quantity: 1, price, source_price: price, source_currency: saleCurrency, sale_currency: saleCurrency, discount: 0, total: price, controlled: false, requires_prescription: false, is_misc: true }]);
    setMisc({ name: "", price: "" }); setMiscOpen(false);
    toast.success(t("pos_toast_misc_added"));
  };
  const saveScannedProduct = async () => {
    if (!newProduct.name.trim()) { toast.error("أدخل اسم الصنف"); return; }
    setNewProductBusy(true);
    try {
      const { data } = await api.post("/products/quick-add", { name: newProduct.name.trim(), barcode: newProduct.barcode.trim(), sale_price: Number(newProduct.sale_price) || 0, cost_price: Number(newProduct.cost_price) || 0, stock: Number(newProduct.stock) || 0, min_stock: 0, category: newProduct.category.trim(), categories: newProduct.category.trim() ? [newProduct.category.trim()] : [], currency: saleCurrency, units: [{ name: "قطعة", factor: 1 }] });
      setProducts(ps => [data, ...ps]);
      setNewProductOpen(false); setNewProduct({ name: "", barcode: "", sale_price: "", cost_price: "", stock: "", category: "" });
      addLine(data);
      toast.success("تمت إضافة الصنف وإدراجه في الفاتورة");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setNewProductBusy(false); }
  };

  const loadProducts = async () => { try { const { data } = await api.get("/products"); setProducts(data); } catch{} };
  const loadTopSellers = async () => { try { const { data } = await api.get("/products/top-sellers?limit=12"); setTopSellers(Array.isArray(data) ? data : []); } catch {} };
  const loadCustomers = async () => { try { const { data } = await api.get("/customers"); setCustomers(Array.isArray(data) ? data : []); } catch {} };
  const loadBanks = async () => { try { const { data } = await api.get("/bank-accounts"); setBankAccounts(Array.isArray(data) ? data : []); } catch {} };
  const createCustomer = async () => {
    const name = custSearch.trim();
    if (!name) { toast.error("أدخل اسم العميل"); return; }
    setCreatingCust(true);
    try {
      const { data } = await api.post("/customers", { name, currency: saleCurrency });
      setCustomers(cs => [data, ...cs]);
      setCustId(data.id); setCustName(data.name); setCustSearch("");
      toast.success(`تمت إضافة العميل «${data.name}»`);
    } catch (e) { toast.error(formatErr(e)); } finally { setCreatingCust(false); }
  };
  const loadRates = async () => { try { const { data } = await api.get("/currencies"); setRates(data?.rates || {}); } catch {} };
  const baseCurrency = currentCompany?.base_currency || "SYP";
  const saleCurrency = (paymentBox.currency || baseCurrency).toUpperCase();
  // Rates are stored per-company as: rates[X] = value of 1 unit of X in the BASE currency.
  const convertAmount = (amount, from, to) => {
    const src = (from || baseCurrency).toUpperCase();
    const dst = (to || baseCurrency).toUpperCase();
    if (src === dst) return Number(amount || 0);
    const srcRate = src === baseCurrency ? 1 : Number(rates[src] || 0);
    const dstRate = dst === baseCurrency ? 1 : Number(rates[dst] || 0);
    if (!srcRate || !dstRate) return Number(amount || 0);
    // value_in_base = amount * srcRate ; result = value_in_base / dstRate
    return Number(amount || 0) * srcRate / dstRate;
  };
  const invoiceExchangeRate = saleCurrency === baseCurrency ? 1 : convertAmount(1, saleCurrency, baseCurrency);

  // Refresh the manual rates while POS is open. Existing cart lines are repriced
  // from their original currency; they are never converted from an already
  // converted value, so repeated refreshes do not accumulate rounding errors.
  useEffect(() => {
    const refresh = () => loadRates();
    const timer = window.setInterval(refresh, 30000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  const [costCenters, setCostCenters] = useState([]);
  const [costCenter, setCostCenter] = useState("");
  const [template, setTemplate] = useState({});
  const [scaleCfg, setScaleCfg] = useState(null);
  const [isOffline, setIsOffline] = useState(() => navigator.onLine === false);
  const [pendingOps, setPendingOps] = useState(() => getOfflineQueue().length);
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    const q = () => setPendingOps(getOfflineQueue().length);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("offline-queue-changed", q);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("offline-queue-changed", q);
    };
  }, []);
  const lockedBranch = user?.branch_cost_center || "";
  useEffect(() => {
    api.get("/cost-centers").then(({ data }) => {
      const list = data || [];
      setCostCenters(list);
      // If the employee is bound to a branch that exists here, preset & lock it
      if (lockedBranch && list.some(c => c.name === lockedBranch)) setCostCenter(lockedBranch);
    }).catch(() => {});
    api.get("/company/template").then(({ data }) => setTemplate(data || {})).catch(() => {});
    api.get("/company/scale-config").then(({ data }) => setScaleCfg(data || null)).catch(() => {});
  }, [currentCompany?.id]); // eslint-disable-line

  useEffect(() => {
    setLines((current) => current.map((line) => {
      const source = line.source_currency || line.sale_currency || baseCurrency;
      const native = line.source_price != null ? Number(line.source_price) : Number(line.price || 0);
      const converted = Math.round(convertAmount(native, source, saleCurrency) * 100) / 100;
      return { ...line, price: converted, total: line.quantity * converted - (line.discount || 0), sale_currency: saleCurrency };
    }));
    // Reprice when the fetched exchange-rate map or selected sale currency changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, saleCurrency, baseCurrency]);

  useEffect(() => {
    // Smart redirect based on business_type
    const bType = currentCompany?.business_type;
    if (bType === "restaurant" || bType === "bakery") {
      toast.info(t("pos_restaurant_redirect"), { duration: 3000 });
      nav("/restaurant/pos", { replace: true });
      return;
    }
    if (bType === "clinic") {
      toast.info(t("pos_clinic_redirect"), { duration: 3000 });
      nav("/clinic/appointments", { replace: true });
      return;
    }
    loadProducts();
    loadTopSellers();
    loadRates();
    loadCustomers();
    loadBanks();
    scanRef.current?.focus();
    // eslint-disable-next-line
  }, [currentCompany?.business_type]);

  // Keyboard shortcuts optimized for cashier speed
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" && e.key !== "F1" && e.key !== "F2" && e.key !== "F3" && e.key !== "F9" && e.key !== "F10" && e.key !== "Escape") return;
      if (e.key === "F2") { e.preventDefault(); scanRef.current?.focus(); scanRef.current?.select?.(); }
      else if (e.key === "F3") { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key === "F9" && lines.length > 0) { e.preventDefault(); setPayDialog(true); }
      else if (e.key === "F10" && lines.length > 0) { e.preventDefault(); quickCash(); }
      else if (e.key === "Escape") { setBarcodeVal(""); setSearchTerm(""); setPayDialog(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lines]);

  const addLine = (p) => {
    if (!p) return;
    if (p.stock !== undefined && p.stock !== null && p.stock <= 0) {
      toast.warning(ti("pos_toast_stock_out", { x: p.name }));
    }
    // Pharmacy prescription warning
    if (p.controlled) {
      toast.warning(ti("pos_toast_controlled", { x: p.name }), { duration: 6000 });
    } else if (p.requires_prescription) {
      toast.warning(ti("pos_toast_rx", { x: p.name }), { duration: 5000 });
    }
    const unit = (p.units && p.units[0]) || { name: "قطعة", factor: 1 };
    const productCurrency = (p.currency || baseCurrency).toUpperCase();
    const nativePrice = Number(p.sale_price || 0);
    if (productCurrency !== saleCurrency && (productCurrency !== baseCurrency && !rates[productCurrency] || saleCurrency !== baseCurrency && !rates[saleCurrency])) {
      toast.error(ti("pos_toast_no_fx", { a: productCurrency, b: saleCurrency }));
      return;
    }
    const price = Math.round(convertAmount(nativePrice, productCurrency, saleCurrency) * 100) / 100;
    const idx = lines.findIndex(l => l.product_id === p.id && l.unit_name === unit.name);
    if (idx >= 0) {
      setLines(ls => ls.map((l, i) => i === idx ? { ...l, quantity: l.quantity + 1, qtyStr: String(l.quantity + 1), total: (l.quantity + 1) * l.price - l.discount } : l));
    } else {
      setLines(ls => [...ls, { product_id: p.id, product_name: p.name, barcode: p.barcode, unit_name: unit.name, unit_factor: unit.factor, quantity: 1, price, source_price: nativePrice, source_currency: productCurrency, sale_currency: saleCurrency, discount: 0, total: price, controlled: !!p.controlled, requires_prescription: !!p.requires_prescription }]);
    }
    setBarcodeVal(""); setSearchTerm(""); scanRef.current?.focus();
  };

  // Add a product line with an exact weight (kg) or an embedded final price (from a scale barcode).
  const addWeighedLine = (p, { weightKg, price }) => {
    if (!p) return;
    const productCurrency = (p.currency || baseCurrency).toUpperCase();
    const nativePrice = Number(p.sale_price || 0);
    if (productCurrency !== saleCurrency && (productCurrency !== baseCurrency && !rates[productCurrency] || saleCurrency !== baseCurrency && !rates[saleCurrency])) {
      toast.error(ti("pos_toast_no_fx", { a: productCurrency, b: saleCurrency }));
      return;
    }
    const unitPrice = Math.round(convertAmount(nativePrice, productCurrency, saleCurrency) * 100) / 100;
    let qty, lineTotal;
    if (price != null) {
      // Price embedded: derive weight from unit (per-kg) price when possible
      lineTotal = Math.round(price * 100) / 100;
      qty = unitPrice > 0 ? Math.round((price / unitPrice) * 1000) / 1000 : 1;
    } else {
      qty = Math.round(Number(weightKg) * 1000) / 1000;
      lineTotal = Math.round(qty * unitPrice * 100) / 100;
    }
    if (!qty || qty <= 0) { toast.error("وزن/سعر غير صالح في الباركود"); return; }
    setLines(ls => [...ls, {
      product_id: p.id, product_name: p.name, barcode: p.barcode,
      unit_name: "كغ", unit_factor: 1, quantity: qty, qtyStr: String(qty),
      price: unitPrice, source_price: nativePrice, source_currency: productCurrency,
      sale_currency: saleCurrency, discount: 0, total: lineTotal,
      controlled: !!p.controlled, requires_prescription: !!p.requires_prescription,
    }]);
    toast.success(`${p.name} · ${qty} كغ`);
    setBarcodeVal(""); setSearchTerm(""); scanRef.current?.focus();
  };

  // Look up a product by the item code embedded in a scale barcode.
  // Tries the dedicated PLU field first, then falls back to the barcode field.
  const lookupByItemCode = async (itemCode) => {
    const codes = [itemCode];
    const stripped = String(parseInt(itemCode, 10));
    if (stripped !== itemCode) codes.push(stripped);
    try { const { data } = await api.get(`/products/by-plu/${itemCode}`); if (data) return data; } catch {}
    for (const code of codes) {
      try { const { data } = await api.get(`/products/barcode/${code}`); if (data) return data; } catch {}
    }
    return null;
  };

  const onScan = async (e) => {
    e.preventDefault();
    if (!barcodeVal.trim()) return;
    const raw = barcodeVal.trim();
    // 1) Try the electronic-scale (weight/price embedded) format first
    const parsed = parseScaleBarcode(raw, scaleCfg);
    if (parsed) {
      const prod = await lookupByItemCode(parsed.itemCode);
      if (prod) { addWeighedLine(prod, parsed); return; }
      // fall through to a normal lookup if the item code didn't resolve
    }
    try {
      const { data } = await api.get(`/products/barcode/${raw}`);
      addLine(data);
      // Soft chime
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC && localStorage.getItem("mute_chime") !== "1") {
          const ctx = new AC();
          const osc = ctx.createOscillator(), gain = ctx.createGain();
          osc.type = "sine"; osc.frequency.value = 1200;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.1);
          osc.connect(gain); gain.connect(ctx.destination);
          osc.start(); osc.stop(ctx.currentTime + 0.15);
          setTimeout(() => ctx.close(), 300);
        }
      } catch {}
    } catch {
      if (!quickProductAddEnabled) { toast.error("الإضافة السريعة للمنتجات مقيدة لهذه الشركة"); return; }
      setNewProduct(f => ({ ...f, barcode: raw })); setNewProductOpen(true);
    }
  };

  const updateQty = (i, delta) => {
    setLines(ls => ls.map((l, idx) => {
      if (idx !== i) return l;
      const step = isWeightUnit(l.unit_name) ? (delta > 0 ? 0.05 : -0.05) : delta;
      const q = Math.max(0, Math.round((l.quantity + step) * 1000) / 1000);
      return { ...l, quantity: q, qtyStr: String(q), total: q * l.price - l.discount };
    }).filter(l => l.quantity > 0));
  };
  const setQty = (i, raw) => {
    // Keep the raw string so decimals like "0.2" type smoothly; derive numeric qty for totals.
    const q = Math.max(0, parseFloat(raw) || 0);
    setLines(ls => ls.map((l, idx) => idx !== i ? l : { ...l, quantity: q, qtyStr: raw, total: q * l.price - l.discount }));
  };
  const setPrice = (i, raw) => {
    const price = Math.max(0, parseFloat(raw) || 0);
    setLines(ls => ls.map((l, idx) => {
      if (idx !== i) return l;
      const sourceCurrency = (l.source_currency || saleCurrency).toUpperCase();
      const sourcePrice = sourceCurrency === saleCurrency
        ? price
        : convertAmount(price, saleCurrency, sourceCurrency);
      return { ...l, price, priceStr: raw, source_price: sourcePrice, total: l.quantity * price - (l.discount || 0) };
    }));
  };
  // Set an exact quantity (used by the weight quick-select chips)
  const setQtyExact = (i, v) => {
    const q = Math.max(0, Math.round(v * 1000) / 1000);
    setLines(ls => ls.map((l, idx) => idx !== i ? l : { ...l, quantity: q, qtyStr: String(q), total: q * l.price - l.discount }));
  };
  // Set quantity from a target MONEY amount (weight = amount / price-per-kg)
  const setByMoney = (i, amount) => {
    setLines(ls => ls.map((l, idx) => {
      if (idx !== i) return l;
      if (!l.price || l.price <= 0) { toast.error(t("pos_toast_no_price")); return l; }
      const q = Math.max(0, Math.round((amount / l.price) * 1000) / 1000);
      return { ...l, quantity: q, qtyStr: String(q), total: q * l.price - l.discount };
    }));
  };
  const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

  const changeSaleCurrency = (patch) => {
    const next = (patch.currency || saleCurrency).toUpperCase();
    setPaymentBox(prev => ({ ...prev, ...patch, currency: next }));
    setReceived("");
    setLines(ls => ls.map(l => {
      const source = l.source_currency || l.sale_currency || baseCurrency;
      const native = l.source_price != null ? l.source_price : l.price;
      const converted = Math.round(convertAmount(native, source, next) * 100) / 100;
      return { ...l, price: converted, total: l.quantity * converted - l.discount, sale_currency: next };
    }));
  };

  const subtotal = useMemo(() => lines.reduce((s, l) => s + l.total, 0), [lines]);
  const voucherDiscount = useMemo(() => voucher ? Math.round(subtotal * (voucher.discount_percent / 100) * 100) / 100 : 0, [voucher, subtotal]);
  const netBeforeVat = useMemo(() => Math.max(0, subtotal - voucherDiscount), [subtotal, voucherDiscount]);
  const companyVatRate = +(currentCompany?.vat_rate || 0);
  const vatAmount = useMemo(() => +(netBeforeVat * companyVatRate / 100).toFixed(2), [netBeforeVat, companyVatRate]);
  const total = useMemo(() => +(netBeforeVat + vatAmount).toFixed(2), [netBeforeVat, vatAmount]);
  const change = payMethod === "cash" && received !== "" ? Math.max(0, (+received || 0) - total) : 0;

  const applyVoucher = async () => {
    if (!voucherCode.trim()) return;
    try {
      const { data } = await api.post(`/loyalty/vouchers/${voucherCode.trim().toUpperCase()}/validate`);
      setVoucher({ code: data.voucher.code, discount_percent: data.voucher.discount_percent });
      toast.success(ti("pos_toast_voucher_ok", { p: data.voucher.discount_percent }));
      setVoucherCode("");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeVoucher = () => { setVoucher(null); setVoucherCode(""); };

  const filteredProducts = useMemo(() => {
    const t = (searchTerm || "").toLowerCase();
    if (!t) return products.slice(0, 24);
    return products.filter(p => p.name.toLowerCase().includes(t) || (p.barcode || "").includes(searchTerm)).slice(0, 24);
  }, [products, searchTerm]);

  const doSave = async (paidAmount, method = "cash") => {
    if (busy) return;
    if (lines.length === 0) return toast.error(t("pos_toast_add_one"));
    setBusy(true);
    const idempotencyKey = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `pos-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const body = {
      kind: "sale",
      party_id: method === "credit" ? custId : null,
      party_name: method === "credit" ? (custName || "عميل آجل") : "عميل نقدي",
      payment_type: method,
      paid_amount: method === "cash" ? Math.min(paidAmount, total) : paidAmount,
      discount_percent: 0, discount_amount: voucherDiscount || 0,
      currency: saleCurrency,
      exchange_rate: invoiceExchangeRate,
      lines, notes: voucher ? `كوبون ولاء: ${voucher.code}` : "",
      cash_box_id: paymentBox.cash_box_id || user?.default_cash_box_id || null,
      bank_account_id: method === "bank" ? (bankId || null) : null,
      payments: null,
      return_reason: null,
      ref_invoice_id: null,
      warehouse_id: (costCenters.find(c => c.name === costCenter)?.warehouse_id) || null,
      cost_center: costCenter || "",
      vat_rate: null,
      idempotency_key: idempotencyKey,
    };
    try {
      const { data } = await api.post("/invoices", body);
      setLastInvoice(data);
      // Mark voucher as used if applied
      if (voucher) {
        try { await api.post(`/loyalty/vouchers/${voucher.code}/use`, { invoice_id: data.id, invoice_number: data.number }); } catch {}
      }
      toast.success(ti("pos_toast_sold", { n: data.number }), {
        action: { label: t("print"), onClick: () => printReceipt(data) },
        duration: 4000,
      });
      // Reset
      setLines([]); setBarcodeVal(""); setSearchTerm(""); setReceived(""); setPayMethod("cash"); setPayDialog(false);
      setVoucher(null); setVoucherCode("");
      setCustId(null); setCustName(""); setCustSearch("");
      setBankId("");
      loadProducts();
      loadTopSellers();
      scanRef.current?.focus();
    } catch (e) {
      if (e?.code === "OFFLINE_QUEUED") {
        // Offline: the sale is safely queued and will sync when back online.
        const localInv = {
          ...body, number: "محلي (بانتظار المزامنة)",
          created_at: new Date().toISOString(), total, offline: true,
        };
        setLastInvoice(localInv);
        toast.success("تم حفظ البيع محليًا — ستتم المزامنة تلقائياً عند عودة الإنترنت", {
          action: { label: t("print"), onClick: () => printReceipt(localInv) },
          duration: 5000,
        });
        setLines([]); setBarcodeVal(""); setSearchTerm(""); setReceived(""); setPayMethod("cash"); setPayDialog(false);
        setVoucher(null); setVoucherCode("");
        loadProducts(); loadTopSellers();
        scanRef.current?.focus();
      } else {
        toast.error(formatErr(e));
      }
    }
    finally { setBusy(false); }
  };

  // F10: quick cash checkout — mark full paid, save + print
  const quickCash = () => doSave(total, "cash");

  const confirmPayment = () => {
    if (payMethod === "cash") {
      const rec = +received || total;
      if (rec < total && !window.confirm(ti("pos_less_than_total", { r: rec.toLocaleString("ar-EG"), t: total.toLocaleString("ar-EG") }))) return;
      doSave(Math.min(rec, total), "cash");
    } else if (payMethod === "bank") {
      if (!bankId) { toast.error("اختر الحساب البنكي لاستلام الحوالة"); return; }
      doSave(total, "bank");
    } else {
      if (!custId) { toast.error("اختر العميل أو أضِفه للبيع الآجل"); return; }
      doSave(0, "credit");
    }
  };

  const printReceipt = async (inv) => {
    // If thermal printer already connected once this session, try direct print
    if (thermalSupported()) {
      try {
        await printThermal({
          header: [currentCompany?.name || "متجر", `فاتورة #${inv.number}`, new Date(inv.created_at).toLocaleString("ar-EG")],
          lines: (inv.lines || []).map(l => ({ name: l.product_name, qty: l.quantity, price: l.total })),
          total: inv.total,
          currency: inv.currency || "SYP",
          footer: ["شكراً لزيارتكم", `الكاشير: ${inv.created_by_name || ""}`],
        });
        toast.success("تم الإرسال للطابعة الحرارية");
        return;
      } catch (e) {
        // Fallback to HTML print silently on first failure or user cancel
        if (!String(e.message || "").includes("No port")) toast.info("سيتم استخدام الطباعة العادية");
      }
    }
    const w = window.open("", "_blank", "width=350,height=600");
    const rows = inv.lines.map(l => `<tr><td>${l.product_name}</td><td style="text-align:center">${l.quantity}</td><td style="text-align:end">${l.price.toLocaleString("ar-EG")}</td><td style="text-align:end">${l.total.toLocaleString("ar-EG")}</td></tr>`).join("");
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
    const logoSrc = template.logo_url ? (template.logo_url.startsWith("http") ? template.logo_url : BACKEND + template.logo_url) : "";
    w.document.write(`
      <html dir="rtl"><head><title>إيصال ${inv.number}</title>
      <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
      <style>
        @page { size: 80mm auto; margin: 3mm; }
        body{font-family:Cairo,Arial;font-size:12px;width:74mm;margin:0 auto;color:#000}
        h1{font-size:15px;margin:2px 0;text-align:center;font-weight:bold}
        table{width:100%;border-collapse:collapse;margin-top:6px}
        th,td{padding:2px 3px;font-size:11px;text-align:start;border-bottom:1px dashed #999}
        .total{font-size:15px;font-weight:bold;text-align:end;margin-top:6px;border-top:2px solid #000;padding-top:4px}
        .row{display:flex;justify-content:space-between;padding:1px 0;font-size:11px}
        .center{text-align:center}
        .logo{display:block;max-width:60mm;max-height:22mm;margin:0 auto 4px;object-fit:contain}
        .terms{margin-top:6px;border-top:1px dashed #999;padding-top:4px;font-size:10px;text-align:center;color:#333}
        .qr{text-align:center;margin-top:8px;border-top:1px dashed #999;padding-top:6px}
        canvas{margin:0 auto}
      </style></head><body>
      ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="logo" />` : ""}
      <h1>${currentCompany?.name || "متجر"}</h1>
      ${template.header_text ? `<div class="center" style="font-size:10px">${String(template.header_text).replace(/</g,"&lt;")}</div>` : ""}
      <div class="center" style="font-size:10px">إيصال بيع #${inv.number}</div>
      <div class="center" style="font-size:10px">${new Date(inv.created_at).toLocaleString("ar-EG")}</div>
      <div class="center" style="font-size:10px">كاشير: ${user?.name || ""}</div>
      <table><thead><tr><th>الصنف</th><th style="text-align:center">كمية</th><th style="text-align:end">سعر</th><th style="text-align:end">مجموع</th></tr></thead>
      <tbody>${rows}</tbody></table>
      ${inv.vat_amount ? `<div class="row"><span>الفرعي</span><span>${(inv.net_before_vat||inv.subtotal||0).toLocaleString("ar-EG")}</span></div><div class="row"><span>ض.ق.م (${inv.vat_rate}%)</span><span>${inv.vat_amount.toLocaleString("ar-EG")}</span></div>` : ""}
      <div class="total">الإجمالي: ${inv.total.toLocaleString("ar-EG")} ${inv.currency}</div>
      <div class="row"><span>المدفوع</span><span>${(inv.paid_amount||0).toLocaleString("ar-EG")}</span></div>
      ${inv.remaining ? `<div class="row" style="color:#DC2626;font-weight:bold"><span>المتبقي</span><span>${inv.remaining.toLocaleString("ar-EG")}</span></div>` : ""}
      ${template.payment_terms ? `<div class="terms">${String(template.payment_terms).replace(/</g,"&lt;")}</div>` : ""}
      <div class="qr"><canvas id="qr" style="width:70px;height:70px"></canvas><div style="font-size:9px;color:#666;margin-top:2px">امسح لإنشاء مرتجع</div></div>
      <div class="center" style="margin-top:6px;font-size:10px">${template.footer_text ? String(template.footer_text).replace(/</g,"&lt;") : "شكراً لزيارتكم"}</div>
      <script>
        window.onload = () => {
          try { if (window.QRCode) QRCode.toCanvas(document.getElementById("qr"), String(${JSON.stringify(inv.number)}), { width: 70, margin: 1 }); } catch(e) {}
          setTimeout(()=>window.print(), 250);
        };
      </script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <>
      {(isOffline || pendingOps > 0) && (
        <div
          className={`mb-2 flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-semibold ${isOffline ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
          data-testid="pos-offline-banner"
        >
          <span className="flex items-center gap-2">
            <CloudOff className="w-4 h-4 shrink-0" />
            {isOffline
              ? `تعمل دون إنترنت — البيع يُحفظ محلياً${pendingOps > 0 ? ` · ${pendingOps} عملية محفوظة` : ""}`
              : `${pendingOps} عملية بانتظار المزامنة مع الخادم`}
          </span>
          <button type="button" onClick={() => nav("/sync-queue")} className="underline text-xs hover:opacity-80 shrink-0" data-testid="pos-offline-banner-link">
            عرض التفاصيل
          </button>
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-4 min-h-[calc(100dvh-8rem)] lg:h-[calc(100vh-7rem)]" data-testid="retail-pos">
      {/* Cart Panel - Right (RTL start) */}
      <div className="lg:col-span-5 flex flex-col min-h-[560px] lg:min-h-0 border-2 border-primary/20 rounded-xl bg-card shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-l from-primary to-primary/80 text-primary-foreground px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><ShoppingCart className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-base leading-tight">{t("pos_retail_title")}</div>
              <div className="text-[10px] opacity-90">Retail POS</div>
            </div>
          </div>
          <div className="text-xs font-mono opacity-95">{lines.length} {t("pos_items")}</div>
        </div>

        {/* Barcode scan bar */}
        <form onSubmit={onScan} className="p-3 border-b bg-muted/30 flex items-center gap-2">
          <ScanBarcode className="w-5 h-5 text-primary flex-shrink-0" />
          <Input
            ref={scanRef}
            value={barcodeVal}
            onChange={(e) => setBarcodeVal(e.target.value)}
            placeholder={t("pos_scan_ph")}
            className="text-base font-mono h-11 border-primary/30 focus:border-primary"
            data-testid="pos-barcode-input"
            autoFocus
          />
          <Button type="button" variant="outline" size="icon" onClick={() => setCamOpen(true)} className="h-11 w-11" data-testid="pos-camera-btn">
            <Camera className="w-4 h-4" />
          </Button>
        </form>

        {/* Park / resume toolbar */}
        <div className="px-3 py-2 border-b bg-background flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setMiscOpen(true)} data-testid="pos-misc-btn">
            <Plus className="w-4 h-4" /> {t("pos_misc_short")}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs flex-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={holdSale} data-testid="pos-hold-btn">
            <PauseCircle className="w-4 h-4" /> {t("pos_hold")}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs flex-1 relative" onClick={() => setHeldOpen(true)} data-testid="pos-held-btn">
            <RotateCcw className="w-4 h-4" /> {t("pos_held_btn")}
            {held.length > 0 && <span className="absolute -top-1.5 -start-1.5 min-w-5 h-5 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold grid place-items-center" data-testid="pos-held-count">{held.length}</span>}
          </Button>
        </div>

        {/* Line items */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="pos-cart-items">
          {lines.length === 0 ? (
            <div className="h-full grid place-items-center text-center text-muted-foreground p-6">
              <div>
                <ShoppingCart className="w-16 h-16 mx-auto opacity-20 mb-3" />
                <div className="text-sm">{t("pos_empty_hint")}</div>
                <div className="text-xs mt-2 opacity-60">{t("pos_shortcuts")}</div>
              </div>
            </div>
          ) : lines.map((l, i) => (
            <div key={i} className={`flex flex-col gap-1.5 p-2 rounded-lg border bg-background hover:border-primary/40 transition ${l.controlled ? "border-red-300 bg-red-50/50" : l.requires_prescription ? "border-amber-300 bg-amber-50/40" : ""}`} data-testid={`pos-line-${i}`}>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-base leading-snug break-words flex items-center gap-1">
                    {l.controlled && <span className="text-red-600 text-[10px] px-1.5 py-0.5 bg-red-100 rounded font-bold flex-shrink-0" title={t("pos_controlled_title")}>⚠️℞</span>}
                    {!l.controlled && l.requires_prescription && <span className="text-amber-700 text-[10px] px-1.5 py-0.5 bg-amber-100 rounded font-bold flex-shrink-0" title={t("pos_rx_title")}>℞</span>}
                    {isWeightUnit(l.unit_name) && <span className="text-emerald-700 text-[10px] px-1.5 py-0.5 bg-emerald-100 rounded font-bold flex-shrink-0" title={t("pos_weight_sell")}>⚖️ {t("pos_weight_badge")}</span>}
                    <span className="break-words">{l.product_name}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <label htmlFor={`pos-price-${i}`} className="text-xs font-bold text-muted-foreground whitespace-nowrap">{isWeightUnit(l.unit_name) ? "السعر / كغ" : "سعر البيع"}</label>
                    <Input
                      id={`pos-price-${i}`}
                      value={l.priceStr ?? l.price}
                      onChange={(e) => setPrice(i, e.target.value)}
                      inputMode="decimal"
                      className="h-10 w-28 text-center font-mono text-base font-bold border-primary/40"
                      aria-label={`تعديل سعر ${l.product_name}`}
                      data-testid={`pos-price-${i}`}
                    />
                    <span className="text-sm text-muted-foreground font-mono">× {l.quantity}{isWeightUnit(l.unit_name) ? t("pos_kg") : ""} = <b className="text-foreground">{l.total.toLocaleString("ar-EG")}</b></span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => updateQty(i, -1)} data-testid={`pos-dec-${i}`}><Minus className="w-3 h-3" /></Button>
                  <Input value={l.qtyStr ?? l.quantity} onChange={(e) => setQty(i, e.target.value)} inputMode="decimal" className="w-16 h-9 text-center font-mono text-base" data-testid={`pos-qty-${i}`} />
                  <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => updateQty(i, 1)} data-testid={`pos-inc-${i}`}><Plus className="w-3 h-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive hover:bg-destructive/10" onClick={() => removeLine(i)} data-testid={`pos-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
              {isWeightUnit(l.unit_name) && (
                <div className="flex flex-wrap gap-1" data-testid={`pos-weight-chips-${i}`}>
                  {WEIGHT_PRESETS.map(w => (
                    <button
                      key={w.v}
                      type="button"
                      onClick={() => setQtyExact(i, w.v)}
                      className={`px-2 py-1 rounded-md text-xs font-bold border transition ${Math.abs((l.quantity || 0) - w.v) < 0.0005 ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}
                      data-testid={`pos-weight-chip-${i}-${w.v}`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              )}
              {isWeightUnit(l.unit_name) && (
                <div className="flex flex-wrap items-center gap-1" data-testid={`pos-money-chips-${i}`}>
                  <span className="text-[10px] text-muted-foreground font-bold ms-0.5">{t("pos_by_value")}</span>
                  {MONEY_PRESETS.map(a => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setByMoney(i, a)}
                      className={`px-2 py-1 rounded-md text-xs font-bold border transition ${Math.abs((l.total || 0) - a) < 0.5 ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}
                      data-testid={`pos-money-chip-${i}-${a}`}
                    >
                      {a.toLocaleString("ar-EG")} {saleCurrency}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {lines.some(l => l.controlled || l.requires_prescription) && (
            <div className="p-2 rounded border-2 border-red-300 bg-red-50 text-xs flex items-start gap-2" data-testid="rx-warning-banner">
              <span className="text-red-600 text-lg leading-none">⚠️</span>
              <div className="flex-1">
                <div className="font-bold text-red-700">{t("pos_rx_banner_title")}</div>
                <div className="text-red-600 text-[11px] mt-0.5">{t("pos_rx_banner_desc")}</div>
              </div>
            </div>
          )}
        </div>

        {/* Total footer */}
        <div className="border-t bg-muted/40 p-3 space-y-2">
          {/* Sale currency is selected before checkout and is also used by F10 quick cash. */}
          <div className="p-2 rounded-lg border bg-background">
            <CashBoxPicker
              value={paymentBox}
              onChange={changeSaleCurrency}
              company={currentCompany}
              label={t("pos_sale_currency_label")}
              compact
              data-testid="pos-sale-currency"
            />
          </div>
          {costCenters.length > 0 && (
            <div className="p-2 rounded-lg border bg-background">
              <label className="text-[11px] text-muted-foreground font-medium block mb-1">مركز التكلفة / الفرع {lockedBranch && <span className="text-primary">(مثبّت)</span>}</label>
              <Select value={costCenter || "__none__"} onValueChange={v => setCostCenter(v === "__none__" ? "" : v)} disabled={!!lockedBranch}>
                <SelectTrigger className="w-full h-8 text-xs" data-testid="pos-cost-center"><SelectValue placeholder="— بدون —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— بدون —</SelectItem>
                  {costCenters.map(c => <SelectItem key={c.id} value={c.name}>{c.name}{c.warehouse_id ? " 🏬" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {/* Loyalty voucher */}
          {!voucher ? (
            <div className="flex gap-1">
              <Input value={voucherCode} onChange={e => setVoucherCode(e.target.value.toUpperCase())} placeholder={t("pos_voucher_ph")} className="h-8 text-xs font-mono" data-testid="pos-voucher-input" onKeyDown={e => e.key === "Enter" && applyVoucher()} />
              <Button size="sm" variant="outline" onClick={applyVoucher} className="h-8 text-xs" data-testid="pos-apply-voucher" disabled={!voucherCode.trim()}>{t("pos_apply")}</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between p-1.5 rounded bg-emerald-50 border border-emerald-200 text-xs">
              <span className="text-emerald-700">🎁 {t("pos_coupon")} <b>{voucher.code}</b> — {t("discount")} {voucher.discount_percent}%</span>
              <button onClick={removeVoucher} className="text-red-500 text-[10px] hover:underline" data-testid="pos-remove-voucher">{t("pos_remove")}</button>
            </div>
          )}
          {(voucherDiscount > 0 || companyVatRate > 0) && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t("pos_subtotal_line")}</span>
              <span className="font-mono">{subtotal.toLocaleString("ar-EG")}</span>
            </div>
          )}
          {voucherDiscount > 0 && (
            <div className="flex items-center justify-between text-xs text-emerald-600">
              <span>{t("pos_voucher_discount")}</span>
              <span className="font-mono">- {voucherDiscount.toLocaleString("ar-EG")}</span>
            </div>
          )}
          {companyVatRate > 0 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="pos-vat-line">
              <span>{t("pos_vat_short")} ({companyVatRate}%)</span>
              <span className="font-mono">{vatAmount.toLocaleString("ar-EG")}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t("pos_total")}</span>
            <span className="text-3xl font-bold text-primary font-mono" data-testid="pos-total">{total.toLocaleString("ar-EG")} <span className="text-xs">{paymentBox.currency || currentCompany?.base_currency || "SYP"}</span></span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              disabled={lines.length === 0 || busy}
              onClick={() => setPayDialog(true)}
              className="h-14 text-base font-bold gap-2"
              data-testid="pos-checkout-btn"
            >
              <CreditCard className="w-5 h-5" />
              {t("pos_pay_f9")}
            </Button>
            <Button
              disabled={lines.length === 0 || busy}
              onClick={quickCash}
              variant="secondary"
              className="h-14 text-base font-bold gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="pos-quick-cash-btn"
            >
              <Zap className="w-5 h-5" />
              {t("pos_quick_cash_f10")}
            </Button>
          </div>
          {lastInvoice && lines.length === 0 && (
            <Button variant="outline" size="sm" className="w-full h-8 text-xs gap-1" onClick={() => printReceipt(lastInvoice)} data-testid="pos-print-last-btn">
              <Printer className="w-3.5 h-3.5" /> {t("pos_print_last")}{lastInvoice.number}
            </Button>
          )}
          {lines.length > 0 && (
            <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={() => { if (window.confirm(t("pos_clear_confirm"))) setLines([]); }}>
              <X className="w-3 h-3 me-1" /> {t("pos_clear_cart")}
            </Button>
          )}
        </div>
      </div>

      {/* Products Panel - Left */}
      <div className="lg:col-span-7 flex flex-col min-h-[620px] lg:min-h-0 border rounded-xl bg-card overflow-hidden">
        <div className="p-3 border-b bg-muted/30 flex items-center gap-2">
          <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Input
            ref={searchRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("pos_search_ph")}
            className="h-12 text-base"
            data-testid="pos-search-input"
          />
        </div>
        {topSellers.length > 0 && !searchTerm && (
          <div className="px-3 py-2 border-b bg-amber-50/40" data-testid="pos-top-sellers">
            <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 mb-1.5"><Flame className="w-3.5 h-3.5" /> {t("pos_top_sellers")}</div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {topSellers.map(p => {
                const out = p.stock !== undefined && p.stock !== null && p.stock <= 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => addLine(p)}
                    disabled={out}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full border border-amber-300 bg-white text-xs font-medium hover:bg-amber-100 hover:border-amber-400 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed max-w-[10rem] truncate"
                    title={p.name}
                    data-testid={`pos-top-seller-${p.id}`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredProducts.length === 0 ? (
            <div className="h-full grid place-items-center text-muted-foreground text-sm">{t("pos_no_products")}</div>
          ) : (
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filteredProducts.map(p => (
                <button
                  key={p.id}
                  onClick={() => addLine(p)}
                  disabled={p.stock !== undefined && p.stock !== null && p.stock <= 0}
                  className="text-start p-4 rounded-xl border bg-background hover:border-primary hover:shadow-md hover:-translate-y-0.5 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed min-h-[190px]"
                  data-testid={`pos-product-${p.id}`}
                >
                  {p.image_url ? (
                    <div className="w-full aspect-square rounded-lg overflow-hidden bg-muted mb-3">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-full aspect-square rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 grid place-items-center mb-3">
                      <ShoppingCart className="w-8 h-8 text-primary/40" />
                    </div>
                  )}
                  <div className="text-base font-bold leading-snug break-words min-h-[2.75rem]">{p.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-base font-bold text-primary font-mono">{(Math.round(convertAmount(Number(p.sale_price || 0), p.currency || baseCurrency, saleCurrency) * 100) / 100).toLocaleString("ar-EG")}</span>
                    {p.stock !== undefined && p.stock !== null && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${p.stock <= 0 ? "bg-red-100 text-red-700" : p.stock < 5 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {p.stock}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Camera Scanner */}
      <BarcodeScannerDialog open={camOpen} onOpenChange={setCamOpen} onScan={(v) => { setBarcodeVal(v); setCamOpen(false); setTimeout(() => document.querySelector('[data-testid="pos-barcode-input"]')?.closest("form")?.requestSubmit(), 50); }} />

      {/* Quick miscellaneous item */}
      <Dialog open={miscOpen} onOpenChange={setMiscOpen}>
        <DialogContent className="max-w-sm" data-testid="pos-misc-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-emerald-600" /> {t("pos_misc_title")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">{t("pos_misc_desc_label")}</label>
              <Input value={misc.name} onChange={e => setMisc({ ...misc, name: e.target.value })} placeholder={t("pos_misc_default")} data-testid="pos-misc-name" />
            </div>
            <div>
              <label className="text-xs font-medium">{t("pos_amount_label")} ({saleCurrency})</label>
              <Input type="number" value={misc.price} onChange={e => setMisc({ ...misc, price: e.target.value })} placeholder="0" autoFocus data-testid="pos-misc-price" onKeyDown={e => e.key === "Enter" && addMiscItem()} />
            </div>
            <div className="text-[11px] text-muted-foreground">{t("pos_misc_note")}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMiscOpen(false)}>{t("cancel")}</Button>
            <Button onClick={addMiscItem} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pos-misc-add">{t("pos_misc_add")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newProductOpen} onOpenChange={setNewProductOpen}>
        <DialogContent className="max-w-md" data-testid="pos-new-product-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanBarcode className="w-5 h-5 text-primary" /> باركود غير مسجل — إضافة صنف</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded bg-muted p-2 text-xs font-mono" dir="ltr">{newProduct.barcode}</div>
            <div><label className="text-xs font-medium">اسم الصنف</label><Input autoFocus value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} data-testid="pos-new-product-name" /></div>
            <div className="grid grid-cols-2 gap-2"><div><label className="text-xs font-medium">سعر البيع</label><Input type="number" value={newProduct.sale_price} onChange={e => setNewProduct({ ...newProduct, sale_price: e.target.value })} data-testid="pos-new-product-sale" /></div><div><label className="text-xs font-medium">سعر التكلفة</label><Input type="number" value={newProduct.cost_price} onChange={e => setNewProduct({ ...newProduct, cost_price: e.target.value })} /></div></div>
            <div className="grid grid-cols-2 gap-2"><div><label className="text-xs font-medium">الكمية الافتتاحية</label><Input type="number" value={newProduct.stock} onChange={e => setNewProduct({ ...newProduct, stock: e.target.value })} /></div><div><label className="text-xs font-medium">التصنيف</label><Input value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} placeholder="اختياري" /></div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewProductOpen(false)}>إلغاء</Button><Button onClick={saveScannedProduct} disabled={newProductBusy} data-testid="pos-new-product-save">{newProductBusy ? "جارٍ الحفظ..." : "حفظ وإضافة للفاتورة"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Held (parked) sales */}
      <Dialog open={heldOpen} onOpenChange={setHeldOpen}>
        <DialogContent className="max-w-md" data-testid="pos-held-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PauseCircle className="w-5 h-5 text-amber-600" /> {t("pos_held_title")}</DialogTitle></DialogHeader>
          {held.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">{t("pos_no_held")}</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 pb-2 border-b">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
                  <Checkbox checked={heldAllSelected} onCheckedChange={toggleHeldAll} data-testid="pos-held-select-all" />
                  تحديد الكل ({selHeld.length}/{held.length})
                </label>
                <Button size="sm" className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={saveHeldSelected} disabled={savingHeld || selHeld.length === 0} data-testid="pos-held-save-selected">
                  <CheckCircle2 className="w-4 h-4" /> حفظ المحدد {selHeld.length ? `(${selHeld.length})` : ""}
                </Button>
              </div>
              <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                {held.map(h => (
                  <div key={h.id} className="flex items-center gap-2 p-3 rounded-lg border bg-background" data-testid={`pos-held-item-${h.id}`}>
                    <Checkbox checked={selHeld.includes(h.id)} onCheckedChange={() => toggleHeldOne(h.id)} data-testid={`pos-held-check-${h.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">{h.count} {t("pos_items")} · {Math.round(h.total).toLocaleString("ar-EG")} {h.currency}</div>
                      <div className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString("ar-EG")}</div>
                    </div>
                    <Button size="sm" className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => resumeSale(h)} data-testid={`pos-resume-${h.id}`}><RotateCcw className="w-3.5 h-3.5" /> {t("pos_resume")}</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteHeld(h.id)} data-testid={`pos-delete-held-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <RetailPOSPaymentDialog
        payDialog={payDialog}
        setPayDialog={setPayDialog}
        payMethod={payMethod}
        setPayMethod={setPayMethod}
        paymentBox={paymentBox}
        changeSaleCurrency={changeSaleCurrency}
        currentCompany={currentCompany}
        bankAccounts={bankAccounts}
        bankId={bankId}
        setBankId={setBankId}
        saleCurrency={saleCurrency}
        convertAmount={convertAmount}
        total={total}
        received={received}
        setReceived={setReceived}
        change={change}
        custId={custId}
        custName={custName}
        setCustId={setCustId}
        setCustName={setCustName}
        custSearch={custSearch}
        setCustSearch={setCustSearch}
        customers={customers}
        createCustomer={createCustomer}
        creatingCust={creatingCust}
        confirmPayment={confirmPayment}
        busy={busy}
        t={t}
      />
    </div>
    </>
  );
}
